import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Form, Icon } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { PATHS } from "router/paths";

import type { LoginFormPayload } from "modules/Login/components/LoginForm";

type LoginFormContentProps = {
  errorMsg: string;
  showPassword: boolean;
  isModalOpen: boolean;
  isLoginPending: boolean;
  setErrorMsg: (errorMsg: string) => void;
  setShowPassword: (showPassword: boolean) => void;
};

const FormHelper = createFormHelper<LoginFormPayload>();

export const LoginFormContent = ({ isModalOpen, errorMsg, setErrorMsg, showPassword, setShowPassword, isLoginPending }: LoginFormContentProps) => {
  const { t } = useTranslation();

  const { watch } = FormHelper.useFormContext();
  const [username, password] = watch(["username", "password"]);

  useEffect(() => setErrorMsg(""), [username, password, setErrorMsg]);

  return (
    <>
      <Form.Field error={errorMsg !== ""}>
        <FormHelper.Field.Input
          name="username"
          inputProps={{
            id: "username",
            icon: "user",
            iconPosition: "left",
            type: "text",
            placeholder: t("v8_login", "Login"),
            fluid: true,
            autoComplete: "username",
            className: errorMsg ? "" : "icon-green"
          }}
        />
      </Form.Field>
      <div className="mt-15">
        <Form.Field className="password" error={Boolean(errorMsg)}>
          <FormHelper.Field.Input
            name="password"
            inputProps={{
              id: "password",
              icon: "key",
              iconPosition: "left",
              placeholder: t("v8_password", "Password"),
              type: showPassword ? "text" : "password",
              fluid: true,
              autoComplete: errorMsg ? "new-password" : "current-password",
              className: errorMsg ? "" : "icon-green"
            }}
          />
          <Icon
            className={showPassword ? "eye grey password-icon" : "eye slash grey password-icon"}
            onMouseDown={() => setShowPassword(true)}
            onMouseUp={() => setShowPassword(false)}
          />
        </Form.Field>
      </div>

      {!isModalOpen && (
        <div className="mt-15 reset-password-container">
          <Link to={PATHS.RESET_PASSWORD_REQUEST}>{t("v8_forgot_username_password", "Forgot username / password?")}</Link>
        </div>
      )}

      <div className="mt-25 mb-20">
        <Form.Button disabled={isLoginPending} loading={isLoginPending} type="submit" id="submit-login" color="green" fluid>
          {t("v8_sign_in", "Sign in")}
        </Form.Button>

        {errorMsg !== "" && (
          <div className="mt-15">
            <Form.Field error>
              <Form.Input value={errorMsg} readOnly />
            </Form.Field>
          </div>
        )}
      </div>
    </>
  );
};
